"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow, StartIcon, StopSign, transitionTime, useIsDesktop } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { useTypingAnimation } from "../hooks/useTypingAnimation"
import { useAnchorScroll } from "../useAnchorScroll"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
  locale?: string
}

const BP_DESKTOP = "@media (min-width: 1366px)"

// --- Styled Components (mobile-first, breakpoints.up only) ---

const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding: 40px 0;
  gap: 24px;
  min-height: auto;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 90px 120px;
    gap: 0;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 90px 240px;
  }
`

const SliderWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  order: 2;
  padding: 0 20px;

  .swiper {
    width: 100%;
    height: 326px;
    position: relative;
    padding-block: 0;
  }

  .swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
    pointer-events: none;
  }

  .swiper-slide-active {
    opacity: 1 !important;
    pointer-events: auto;
    z-index: 2;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 450px;
    order: unset;
    padding: 0;

    .swiper {
      height: 450px;
    }
  }

  ${BP_DESKTOP} {
    width: 640px;
    .swiper {
      height: 623px;
    }
  }
`

const StaticImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 326px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 450px;
  }

  ${BP_DESKTOP} {
    height: 623px;
  }
`

const SwiperContainer = styled.div`
  position: relative;
  width: 100%;
  height: 326px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 450px;
  }

  ${BP_DESKTOP} {
    height: 623px;
  }
`

const SliderControls = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 10;
`

const ArrowBtn = styled.button`
  background: var(--comp-bg, white);
  border: 1px solid var(--comp-text, black);
  border-radius: 50px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    color: var(--comp-text);
    path {
      fill: var(--comp-text);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const PlayPauseBtn = styled.button`
  background: white;
  border: none;
  border-radius: 40px;
  padding: 10px;
  opacity: 0.4;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  top: 20px;
  inset-inline-end: 20px;
  z-index: 15;
  background: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const PagerContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
`

const PagerDot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 100px;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 12px;
    height: 12px;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const ContentColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 0 40px;
  order: 3;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 520px;
    padding: 0 20px;
    order: unset;
  }
`

const MobileLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 20px;
  order: 1;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const LogosRow = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 4px 0;
  }
`

const Divider = styled.hr<{ hideOnMobile?: boolean }>`
  width: 100%;
  border: none;
  border-top: 1px solid var(--comp-text);
  opacity: 0.2;
  margin: 8px 0;

  ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 16px 0;
    display: block;
  }
`

const Headline = styled.h1`
  font-size: 60px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  color: var(--comp-heading);
  margin: 0;
  width: 100%;
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 72px;
    line-height: 1;
  }
`

const HeadlinePlaceholder = styled.span`
  display: block;
  visibility: hidden;
`

const HeadlineTyped = styled.span`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`

const BodyText = styled.div`
  font-size: 18px;
  font-weight: 300;
  line-height: 1.5;
  text-align: center;
  color: var(--comp-text);
  width: 100%;
  letter-spacing: -0.4px;
  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-weight: 400;
    font-size: 20px;
  }
`

const SecondaryText = styled.div`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  color: var(--comp-text);
  width: 100%;
  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 28px;
  }
`

const CtaButton = styled(Button)`
  && {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 440px;
    }

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }
  }
`

const SecondaryButton = styled(Button)`
  && {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 700;
    text-transform: none;
    background-color: transparent;
    color: var(--inner-bg);
    border: 2px solid var(--inner-bg);

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 440px;
    }

    &:hover {
      background-color: transparent;
      opacity: 0.9;
    }
  }
`

const TextAndButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`

// --- Sub-components ---

function SingleMedia({ item }: { item: any }) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  if (isVideo) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          ref={videoRef}
          src={getUmbracoMediaUrl(url)}
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <VideoPlayPauseBtn
          onClick={() => {
            if (videoRef.current) {
              analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.hero)
              if (paused) videoRef.current.play()
              else videoRef.current.pause()
            }
          }}
          aria-label={
            paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
          }
        >
          {paused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      </div>
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
    />
  )
}

// --- Main Component ---

function HeroOption1({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const logos = extractProperty(data, "heroLogos", true) as any[] | undefined
  const headline = extractProperty(data, "heroHeadline") as string | undefined
  const bodyText = extractProperty(data, "heroBodyText") as string | undefined
  const secondaryText = extractProperty(data, "heroSecondaryText") as string | undefined
  const ctaButton = extractProperty(data, "heroCtaButton") as any[] | undefined
  const secondaryBtn = extractProperty(data, "heroSecondaryButton") as any[] | undefined
  const desktopMedia = extractProperty(data, "heroBackgroundMedia", true) as any[] | undefined
  const mobileMedia = extractProperty(data, "heroBackgroundMediaMobile", true) as any[] | undefined
  const isDesktop = useIsDesktop()
  const mediaItems = isDesktop ? desktopMedia : mobileMedia?.length ? mobileMedia : desktopMedia

  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: headlineRef, inView: headlineInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })
  const { displayed: typedHeadline, full: fullHeadline } = useTypingAnimation(
    headline,
    headlineInView,
  )

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0
  const hasMultipleMedia = hasMedia && mediaItems!.length > 1
  const showArrows = hasMultipleMedia
  const showDots = hasMultipleMedia
  const showPlayPause = hasMultipleMedia

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.hero)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  useEffect(() => {
    if (!containerRef.current || !hasMultipleMedia) return
    const slides = containerRef.current.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.removeAttribute("inert")
        slide.removeAttribute("aria-hidden")
      } else {
        slide.setAttribute("inert", "")
        slide.setAttribute("aria-hidden", "true")
      }
    })
  }, [activeIndex, hasMultipleMedia])

  const ctaLink = ctaButton?.[0]
  const secondaryLink = secondaryBtn?.[0]
  const ctaHref = ctaLink ? getLinkHrefFromMultiUrlPickerItem(ctaLink as any) : undefined
  const secondaryHref = secondaryLink
    ? getLinkHrefFromMultiUrlPickerItem(secondaryLink as any)
    : undefined
  const handleAnchorClick = useAnchorScroll()
  const logoItems = Array.isArray(logos) ? logos : []
  const logoSize = { width: 133, height: 82 }
  const mobileLogoSize = { width: 107, height: 66 }

  return (
    <Section>
      {/* Mobile logos — above slider, hidden on desktop */}
      {logoItems.length > 0 && (
        <MobileLogos>
          {logoItems.map((logo: any, i: number) => (
            <Image
              key={i}
              src={getUmbracoMediaUrl(logo?.url || "/")}
              width={mobileLogoSize.width}
              height={mobileLogoSize.height}
              alt={logo?.altText || ""}
              style={{ objectFit: "contain" }}
            />
          ))}
        </MobileLogos>
      )}

      {/* Content — first in JSX = right side in RTL desktop */}
      <ContentColumn>
        {logoItems.length > 0 && (
          <>
            <LogosRow>
              {logoItems.map((logo: any, i: number) => (
                <Image
                  key={i}
                  src={getUmbracoMediaUrl(logo?.url || "/")}
                  width={logoSize.width}
                  height={logoSize.height}
                  alt={logo?.altText || ""}
                  style={{ objectFit: "contain" }}
                />
              ))}
            </LogosRow>
            {(headline || bodyText || secondaryText) && <Divider hideOnMobile />}
          </>
        )}

        {headline && (
          <>
            <Headline ref={headlineRef}>
              <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
              <HeadlineTyped>{typedHeadline}</HeadlineTyped>
            </Headline>
            {(bodyText || secondaryText) && <Divider hideOnMobile />}
          </>
        )}

        <TextAndButtons>
          {bodyText && (
            <BodyText dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(bodyText, true) }} />
          )}

          {secondaryText && (
            <>
              {bodyText && <Divider />}
              <SecondaryText
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(secondaryText, true),
                }}
              />
            </>
          )}

          {ctaLink && (
            <CtaButton
              variant="contained"
              href={ctaHref}
              onClick={(e) => {
                analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                handleAnchorClick(e, ctaHref)
              }}
              disableElevation
            >
              {ctaLink.name}
            </CtaButton>
          )}

          {secondaryLink && (
            <SecondaryButton
              variant="outlined"
              href={secondaryHref}
              onClick={(e) => {
                analytics.ctaClick(secondaryLink.name, LANDING_ELEMENTS.hero)
                handleAnchorClick(e, secondaryHref)
              }}
            >
              {secondaryLink.name}
            </SecondaryButton>
          )}
        </TextAndButtons>
      </ContentColumn>

      {/* Slider — second in JSX = left side in RTL desktop */}
      {hasMedia && (
        <SliderWrapper ref={containerRef}>
          {mediaItems!.length === 1 ? (
            <StaticImageContainer>
              <SingleMedia item={mediaItems![0]} />
            </StaticImageContainer>
          ) : (
            <SwiperContainer>
              <Swiper
                loop
                onSwiper={setSwiperRef}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.realIndex)
                  const activeSlide = swiper.slides[swiper.activeIndex]
                  const video = activeSlide?.querySelector("video")
                  if (video) {
                    video.currentTime = 0
                    video.play()
                  }
                }}
                modules={[EffectFade, Autoplay]}
                effect="fade"
                speed={transitionTime * 1000}
                style={{ width: "100%", height: "100%" }}
                autoplay={
                  isPlaying
                    ? {
                        delay: 4500,
                        disableOnInteraction: false,
                      }
                    : false
                }
              >
                {mediaItems!.map((item: any, i: number) => (
                  <SwiperSlide key={i}>
                    <SingleMedia item={item} />
                  </SwiperSlide>
                ))}
              </Swiper>

              {showPlayPause && (
                <SliderControls>
                  {showArrows && (
                    <ArrowBtn
                      onClick={() => swiperRef?.slidePrev()}
                      aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
                    >
                      {isRtl ? (
                        <RightArrow fill="currentColor" />
                      ) : (
                        <LeftArrow fill="currentColor" />
                      )}
                    </ArrowBtn>
                  )}

                  <PlayPauseBtn
                    onClick={togglePlayPause}
                    aria-label={
                      isPlaying
                        ? isRtl
                          ? "עצור תנועת הרכיב"
                          : "Stop animation"
                        : isRtl
                        ? "הפעל תנועה רכיב"
                        : "Play animation"
                    }
                  >
                    {isPlaying ? <StopSign /> : <StartIcon />}
                  </PlayPauseBtn>

                  {showArrows && (
                    <ArrowBtn
                      onClick={() => swiperRef?.slideNext()}
                      aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
                    >
                      {isRtl ? (
                        <LeftArrow fill="currentColor" />
                      ) : (
                        <RightArrow fill="currentColor" />
                      )}
                    </ArrowBtn>
                  )}
                </SliderControls>
              )}
            </SwiperContainer>
          )}
          {showDots && (
            <PagerContainer>
              {mediaItems!.map((_: any, i: number) => (
                <PagerDot
                  key={i}
                  active={i === activeIndex}
                  onClick={() => swiperRef?.slideTo(i)}
                  aria-label={isRtl ? `עבור לשקופית ${i + 1}` : `Go to slide ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : undefined}
                />
              ))}
            </PagerContainer>
          )}
        </SliderWrapper>
      )}
    </Section>
  )
}

export default HeroOption1
